import React, { useEffect } from 'react';

import { CardSortResponse } from '@components/ScreenerResults/components';
import { ScreenerChart } from '@components/ScreenerResults/ScreenerChart/components/ScreenerChart';
import { Title } from '@components/ScreenerResults/ScreenerChart/components/shared';
import { CopyURLButton } from 'components/shared/CopyURLButton';
import { removeHash } from 'components/utils';

import { PrototypeTestResponse } from '../components/PrototypeTestResponse/PrototypeTestResponse';
import { TreeTestResponse } from '../components/TreeTestResponse';

import { MatrixChart } from './components/MatrixChart';
import { ChartableType } from './types';

interface ScreenerChartWithTitleProps {
  title: string;
  type: ChartableType;
  answers: ScreenerResponseResultsSummary['answers'];
  id: number;
  blockableId: number | null;
}

export const ScreenerChartWithTitle: React.FC<React.PropsWithChildren<ScreenerChartWithTitleProps>> = ({
  title,
  type,
  answers,
  id,
  blockableId
}) => {
  const total = answers.filter((a) => a).length;

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' });
  };

  useEffect(() => {
    scrollTo(window.location.hash.substring(1));
    removeHash();
  }, []);

  if (['info', 'start_loom', 'stop_loom', 'task', 'permissions'].includes(type)) return null;

  const renderResponse = () => {
    switch (type) {
      case 'prototype_test':
        return blockableId ? (
          <PrototypeTestResponse
            total={total}
            prototypeTestId={blockableId}
            answers={answers as ScreenerResponseAnswerValue<'prototype_test'>[]}
          />
        ) : null;
      case 'card_sort':
        return blockableId ? (
          <CardSortResponse
            total={total}
            blockableId={blockableId}
            answers={answers as ScreenerResponseAnswerValue<'card_sort'>[]}
          />
        ) : null;
      case 'tree_test':
        return blockableId ? (
          <TreeTestResponse
            total={total}
            title={title}
            blockableId={blockableId}
            answers={answers as ScreenerResponseAnswerValue<'tree_test'>[]}
          />
        ) : null;
      case 'matrix':
        return <MatrixChart answers={answers as ScreenerResponseAnswerValue<'matrix'>[]} total={total} />;
      default:
        return <ScreenerChart type={type} answers={answers} total={total} />;
    }
  };

  return (
    <div className='mb-10'>
      <div id={id.toString()} className='mb-1 flex items-center'>
        <Title>{title || 'Untitled question'}</Title>
        <CopyURLButton
          data-testid='copy-url-button'
          className='ml-2'
          small
          link
          icon='link'
          noTippy
          text={`${window.location.href}#${id}`}
          trackEvent='copied_question_link'
        />
      </div>
      {renderResponse()}
    </div>
  );
};
